import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Box, ExternalLink, Loader2, LogIn, Plus, RefreshCw, Search, X } from 'lucide-react';
import { apiService } from '../../services/api';
import CadThumb from './CadThumb';

// CAO du projet dans Fusion : le compte Autodesk d'Innowide (relié une fois par un administrateur), les sources reliées
// (un dossier d'un projet Fusion — chez Innowide, les vrais projets sont les dossiers du projet « Innowide » — ou un
// projet Fusion entier) et les dernières versions enregistrées. Chaque nuit, Deepflow relève les nouvelles versions ; Muse les lit
// avec le reste du projet et met au carnet (parfois au journal) ce qu'elles font avancer (backend : fusion_cad.py).

const when = (iso) => (iso ? new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}).format(new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)) : '');

const kindOf = (type) => {
  const text = String(type || '').toLowerCase();
  if (text.includes('drawing') || text === 'f2d') return 'mise en plan';
  if (text.includes('design') || text === 'f3d') return 'conception';
  return text.split(':').pop() || 'fichier';
};

const CadDialog = ({ projectId, open, onClose, onChange }) => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [projects, setProjects] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => apiService.get(`/projects/${projectId}/fusion`)
    .then((result) => { setData(result); onChange?.(result.links || []); })
    .catch((err) => setError(err.message)), [projectId, onChange]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setData(null);
    load();
  }, [open, load]);

  useEffect(() => {
    if (!open || !data?.connected) return undefined;
    const timer = setTimeout(() => {
      apiService.get(`/projects/${projectId}/fusion/available?q=${encodeURIComponent(query)}`)
        .then(setProjects).catch((err) => setError(err.message));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, data?.connected, query, projectId, data?.links?.length]);

  if (!open) return null;

  const act = async (key, request) => {
    setBusy(key);
    setError(null);
    try {
      await request();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };
  const connect = async () => {
    setBusy('connect');
    try {
      const { authorization_url: url } = await apiService.post('/auth/autodesk/start', { return_to: `/project/${projectId}?cao=1` });
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };
  const keyOf = (p) => `${p.fusion_project_id}:${p.folder_id || ''}`;
  const link = (p) => act(keyOf(p), () => apiService.post(`/projects/${projectId}/fusion`, {
    hub_id: p.hub_id, fusion_project_id: p.fusion_project_id, folder_id: p.folder_id || '' }));
  const unlink = (id) => act(`-${id}`, () => apiService.delete(`/projects/${projectId}/fusion?source_id=${id}`));
  const sync = () => act('sync', async () => {
    await apiService.post(`/projects/${projectId}/fusion/sync`, {});
    await new Promise((resolve) => { setTimeout(resolve, 5000); });
  });

  const links = data?.links || [];
  const recent = data?.recent || [];

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4"
      onClick={(event) => { event.stopPropagation(); onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-t-2xl bg-white p-5 text-slate-800 shadow-2xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><Box size={18} /> CAO du projet (Fusion)</h2>
            <p className="text-xs text-slate-500">
              Chaque nuit, Nexus relève les nouvelles versions enregistrées dans Fusion ; Muse met au carnet (et au journal
              si ça compte) ce qu&apos;elles font avancer.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        {error && <p className="mb-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700"><AlertTriangle size={15} className="mt-0.5 shrink-0" />{error}</p>}
        {!data && !error && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={15} className="animate-spin" /> Chargement…</p>}
        {data && !data.configured && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Fusion n&apos;est pas encore relié à Deepflow : il manque l&apos;application Autodesk sur le serveur.
          </p>
        )}
        {data?.configured && !data.connected && (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-slate-700">
            <p>Deepflow doit d&apos;abord être autorisé à lire les projets Fusion d&apos;Innowide (lecture seule).</p>
            {data.can_connect ? (
              <button type="button" onClick={connect} disabled={busy === 'connect'}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                {busy === 'connect' ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />} Connecter Autodesk
              </button>
            ) : <p className="mt-1 text-xs text-slate-500">Un administrateur doit connecter le compte Autodesk d&apos;Innowide.</p>}
          </div>
        )}
        {data?.connected && data.account && <p className="mb-3 text-xs text-slate-400">Compte Autodesk relié : {data.account}</p>}

        {data && (data.connected || links.length > 0) && (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dossiers Fusion suivis</h3>
            {links.length === 0 && <p className="text-sm text-slate-400">Aucun dossier Fusion suivi pour l&apos;instant.</p>}
            {links.map((l) => (
              <div key={l.id} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{l.name}</p>
                  <p className={`text-xs ${l.error ? 'text-rose-600' : 'text-slate-500'}`}>
                    {l.error || (l.synced_at ? `Relevé le ${when(l.synced_at)} · ${l.versions_7_days} version${l.versions_7_days > 1 ? 's' : ''} sur 7 jours` : 'Premier relevé en cours…')}
                  </p>
                </div>
                <button type="button" onClick={() => unlink(l.id)} disabled={busy === `-${l.id}`}
                  className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50">
                  Retirer
                </button>
              </div>
            ))}
            {links.length > 0 && data.connected && (
              <button type="button" onClick={sync} disabled={busy === 'sync'}
                className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-60">
                <RefreshCw size={13} className={busy === 'sync' ? 'animate-spin' : ''} /> Relever maintenant
              </button>
            )}
          </section>
        )}

        {data?.connected && (
          <section className="mt-5 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Suivre un dossier Fusion</h3>
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 focus-within:border-indigo-300">
              <Search size={15} className="text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Chercher un dossier ou un projet Fusion…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
            </label>
            <div className="max-h-52 space-y-1 overflow-y-auto">
              {projects === null && <p className="flex items-center gap-2 px-1 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Projets Fusion…</p>}
              {projects?.length === 0 && <p className="px-1 text-sm text-slate-400">Aucun dossier Fusion trouvé.</p>}
              {(projects || []).slice(0, 12).map((p) => (
                <button key={keyOf(p)} type="button" onClick={() => link(p)} disabled={busy === keyOf(p)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-indigo-50 disabled:opacity-60">
                  {busy === keyOf(p) ? <Loader2 size={14} className="animate-spin text-indigo-500" /> : <Plus size={14} className="text-indigo-500" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">{p.name}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {p.kind === 'dossier' ? 'Dossier' : 'Projet Fusion entier'}{p.hub ? ` · ${p.hub}` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {(data?.assemblies || []).length > 0 && (
          <section className="mt-5 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conceptions</h3>
              <a href={`/project/${projectId}?tab=materiel`} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800">
                Nomenclatures et liens PLM →
              </a>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {data.assemblies.slice(0, 6).map((a) => (
                <div key={a.item_id} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2">
                  <CadThumb projectId={projectId} itemId={a.item_id} version={a.version} available={a.thumbnail} className="h-12 w-12" />
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-slate-800">{a.name}</p>
                    <p className="truncate text-[11px] text-slate-500">
                      v{a.version} · {a.parts.reduce((n, x) => n + x.qty, 0)} pièces{a.mass ? ` · ${Number(a.mass).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} ${a.mass_unit || ''}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {recent.length > 0 && (
          <section className="mt-5 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dernières versions relevées</h3>
            <ul className="space-y-1.5">
              {recent.slice(0, 15).map((v) => (
                <li key={`${v.file}-${v.version}-${v.at}`} className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="flex items-center gap-1.5 text-sm text-slate-800">
                    <span className="truncate">{v.file}</span>
                    <span className="shrink-0 rounded bg-slate-200 px-1 text-[10px] font-semibold text-slate-600">v{v.version}</span>
                    {v.url && <a href={v.url} target="_blank" rel="noopener noreferrer" aria-label="Ouvrir dans Fusion" className="shrink-0 text-slate-400 hover:text-indigo-600"><ExternalLink size={12} /></a>}
                  </p>
                  {v.description && <p className="text-xs italic text-slate-600">« {v.description} »</p>}
                  <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
                    <span>{v.author}</span>
                    <span>{when(v.at)}</span>
                    <span>{kindOf(v.type)}</span>
                    {v.path && <span className="truncate">{v.path}</span>}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CadDialog;
